import { useSettings } from '@wix/tpa-settings/react';
import React from 'react';
import {
  PaymentMethod,
  type PaymentOption,
  PaymentType,
  ReservedPaymentOptionIds,
  ReservedPaymentOptionLabels,
} from '../../../../../../types/types';
import { DepositOrFullAmountOptionIds } from '../../../../../../utils/payment/paymentOptions/utils';
import { getContent } from '../../../../../../utils/content/content';
import type { SlotService } from '../../../../../../utils/state/types';
import { useFormActions } from '../../../../Hooks/useFormActions';
import { getServiceSlotIdentifier } from '../../../../../../utils';
import {
  getParticipantPayment,
  isOwnPaymentParticipant,
} from '../../../../../../utils/multiParticipantFlow/participantPayment';
import settingsParams from '../../../../settingsParams';
import { ServicePaymentDropdown } from './ServicePaymentDropdown';
import { ServicePaymentRadioButtons } from './ServicePaymentRadioButtons';

export type PaymentFormatProps = {
  service: SlotService;
  // Scopes DOM ids / radio group names so several participants' selections don't clash.
  participantIndex?: number;
  paymentOptions: PaymentOption[];
  label: string;
  selectedId: string;
  onSelect: (id: string) => void;
  getPaymentOptionLabel: (option: PaymentOption) => string;
  buildPaymentOptionId: (option: PaymentOption) => string;
};

export type ServicePaymentOptionsProps = {
  service: SlotService;
  paymentOptions: PaymentOption[];
  label: string;
  isSingleService: boolean;
  participantIndex?: number;
};

export const getPaymentOptionsScope = (
  service: SlotService,
  participantIndex?: number,
) =>
  isOwnPaymentParticipant(participantIndex)
    ? `${getServiceSlotIdentifier(service.nestedSlot)}-p${participantIndex}`
    : getServiceSlotIdentifier(service.nestedSlot);

export const ServicePaymentOptions: React.FC<ServicePaymentOptionsProps> = ({
  service,
  paymentOptions,
  label,
  isSingleService,
  participantIndex,
}) => {
  const { setServiceSelectedPaymentOption } = useFormActions();
  const settings = useSettings();

  const buildPaymentOptionId = (paymentOption: PaymentOption) => {
    return `${paymentOption.id}:${paymentOption.label}`;
  };

  const getPaymentOptionLabel = (paymentOption: PaymentOption) => {
    let optionLabel;
    switch (paymentOption.type) {
      case PaymentType.SINGLE_SESSION: {
        if (paymentOption.label === ReservedPaymentOptionLabels.ONLINE) {
          optionLabel = getContent({
            settings,
            settingsParam: settingsParams.payOnlineText,
          });
        } else if (
          paymentOption.label === ReservedPaymentOptionLabels.OFFLINE
        ) {
          optionLabel = getContent({
            settings,
            settingsParam: settingsParams.payOfflineText,
          });
        } else if (paymentOption.label === ReservedPaymentOptionLabels.FREE) {
          optionLabel = getContent({
            settings,
            settingsParam: settingsParams.freeText,
          });
        } else {
          optionLabel = paymentOption.label;
        }
        break;
      }
      case PaymentType.BUY_PLAN:
      case PaymentType.USE_PLAN: {
        optionLabel = paymentOption.label;
        break;
      }
      default:
        optionLabel = paymentOption.label;
    }

    return optionLabel.toString();
  };

  const findSelectedPaymentOption = (id: string) => {
    return (paymentOptions || []).find(
      (paymentOption) => id === buildPaymentOptionId(paymentOption),
    )!;
  };

  const { selectedPaymentOption, selectedPaymentType: currentPaymentType } =
    getParticipantPayment(service, participantIndex);

  const getSelectedId = () => {
    const selectedPaymentOptionId = selectedPaymentOption.id;
    const selectedPaymentType = currentPaymentType;
    let foundOption;
    if (selectedPaymentOptionId === ReservedPaymentOptionIds.BuyAPricingPlan) {
      foundOption = paymentOptions.find(
        (option) => option.type === PaymentType.BUY_PLAN,
      );
    } else if (
      selectedPaymentOptionId === ReservedPaymentOptionIds.SingleSession
    ) {
      if (selectedPaymentType === PaymentMethod.ONLINE) {
        foundOption = paymentOptions.find(
          (option) =>
            option.type === PaymentType.SINGLE_SESSION &&
            (option.id === DepositOrFullAmountOptionIds.Deposit ||
              option.id === DepositOrFullAmountOptionIds.FullAmount),
        );
        if (!foundOption) {
          foundOption = paymentOptions.find(
            (option) =>
              option.type === PaymentType.SINGLE_SESSION &&
              option.label === ReservedPaymentOptionLabels.ONLINE,
          );
        }
      } else if (selectedPaymentType === PaymentMethod.OFFLINE) {
        foundOption = paymentOptions.find(
          (option) =>
            option.type === PaymentType.SINGLE_SESSION &&
            option.label === ReservedPaymentOptionLabels.OFFLINE,
        );
      } else {
        foundOption = paymentOptions.find(
          (option) => option.type === PaymentType.SINGLE_SESSION,
        );
      }
    } else {
      foundOption = paymentOptions.find(
        (option) => option.id === selectedPaymentOptionId,
      );
    }

    if (foundOption) {
      return buildPaymentOptionId(foundOption);
    }
  };

  const [selectedId, setSelectedId] = React.useState<string>(
    () => getSelectedId()!,
  );
  // Follow selection changes made outside this component (e.g. the default applied to a
  // freshly added participant after its section already mounted).
  React.useEffect(() => {
    const nextId = getSelectedId();
    if (nextId && nextId !== selectedId) {
      setSelectedId(nextId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPaymentOption.id,
    selectedPaymentOption.label,
    currentPaymentType,
  ]);

  const onSelect = (id: string) => {
    setSelectedId(id);
    const option = findSelectedPaymentOption(id);
    if (isOwnPaymentParticipant(participantIndex)) {
      setServiceSelectedPaymentOption(service, option, participantIndex);
    } else {
      setServiceSelectedPaymentOption(service, option);
    }
  };

  if (!selectedId) {
    return null;
  }

  const formatProps: PaymentFormatProps = {
    service,
    participantIndex,
    paymentOptions,
    label,
    selectedId,
    onSelect,
    getPaymentOptionLabel,
    buildPaymentOptionId,
  };

  const ServicePaymentComponent = isSingleService
    ? ServicePaymentRadioButtons
    : ServicePaymentDropdown;
  return <ServicePaymentComponent {...formatProps} />;
};
